// ══════════════════════════════════════════════════════════════════════════════════════════════
//  ABRIR UN ARCHIVO SUBIDO — EN UN TELEFONO TAMBIEN (B-435)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  LO QUE DIJO EL DUENO: «en iPhone no deja abrir los documentos subidos algunas veces, sale como
//  error no se puede abrir este documento». Habia CINCO cosas distintas detras y todas acababan en
//  la misma pantalla en blanco o en el mismo error del sistema, que no explica nada:
//
//   1 · EL TIPO. La pantalla se baja el fichero como blob y un blob de XHR HEREDA el Content-Type
//       de la respuesta. El servidor devolvia `application/octet-stream` para todo lo que no
//       supiera ensenar —y ahi caia hasta una foto, si quien la subio la declaro `image/jpg` en vez
//       de `image/jpeg`—. Un blob sin tipo reconocible ES el «no se puede abrir este documento» de
//       Safari. Eso se arregla en el servidor (services/adjuntosSeguros.js, `tipoReal`).
//   2 · EL ORDEN DE LA PESTANA. Una pestana abierta DESPUES de un `await` ya no cuenta como
//       respuesta a un toque, y el navegador del telefono la bloquea EN SILENCIO: se pulsa Abrir y
//       no pasa absolutamente nada. Aqui la pestana se abre dentro del gesto, en blanco, y se le
//       pone el documento cuando llega.
//   3 · SOLTAR EL BLOB DEMASIADO PRONTO. Si se revoca antes de que la pestana lo haya cargado, en un
//       telefono se queda en blanco y en un ordenador rapido no pasa nunca: el fallo tipico de
//       «algunas veces». Aqui se suelta al minuto, nunca antes.
//   4 · EL PESO. Safari de iOS carga el blob ENTERO en memoria. Con un escaneo grande no lo abre.
//       Por encima del tope, en iOS, no se abre: se descarga y se dice (y en un ordenador no cambia
//       nada, que ahi funciona).
//   5 · LOS DE OFFICE. Safari no sabe ensenar un .docx ni un .xlsx, y no hay arreglo tecnico que
//       valga. Lo que SI se puede es no mandar a la persona contra un error del sistema: se
//       descarga y se le dice, con palabras, que lo abra con su programa.
//
//  LA REGLA QUE RESUME TODO: nunca se sale de aqui en silencio. O se ve el documento, o se descarga
//  Y SE DICE POR QUE. Un error del sistema que el usuario no puede entender ni resolver es peor que
//  no ofrecer el boton.

// EL TIPO QUE ESCRIBE DE VERDAD CADA APARATO, y el oficial equivalente. `image/jpg` NO es
// `image/jpeg`: lo mandan asi los escaneres y los Android, y esa es la causa exacta del «algunas
// veces» del parte — la misma foto se abria o no segun con que se hubiera subido.
//
// EL SERVIDOR TIENE LA SUYA (backend/services/adjuntosSeguros.js) Y NO PUEDEN SEPARARSE. No se
// comparte el fichero a proposito: ese modulo lo carga medio backend y varias guardias copian SOLO
// `backend/` a una carpeta temporal, asi que un `require` que saliera de ahi las mataria a todas.
// Lo que impide que las dos tablas se separen es una guardia que EJECUTA las dos y las compara en
// los DOS sentidos: backend/routes/iphone-abrir-documento-pantalla.test.js, seccion 2.
import { enLaAppNativa, verDocumentoEnLaApp } from './verDocumento'
import { t as i18nT, marca, tDato } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const SINONIMO = {
  'image/jpg': 'image/jpeg', 'image/jpe': 'image/jpeg', 'image/x-jpeg': 'image/jpeg',
  'image/x-png': 'image/png',
  'image/x-ms-bmp': 'image/bmp', 'image/x-windows-bmp': 'image/bmp',
  'image/tif': 'image/tiff', 'image/x-tiff': 'image/tiff', 'image/x-tif': 'image/tiff',
  'image/heic-sequence': 'image/heic', 'image/x-heic': 'image/heic',
  'image/heif-sequence': 'image/heif', 'image/x-heif': 'image/heif',
  'image/dicom': 'application/dicom', 'application/x-dicom': 'application/dicom',
  'application/x-pdf': 'application/pdf', 'application/acrobat': 'application/pdf',
  'applications/vnd.pdf': 'application/pdf', 'text/pdf': 'application/pdf',
  'audio/mp3': 'audio/mpeg', 'audio/mpeg3': 'audio/mpeg', 'audio/x-mpeg': 'audio/mpeg',
  'audio/x-mp4': 'audio/mp4', 'audio/vnd.wave': 'audio/wav', 'audio/wave': 'audio/wav',
  'video/x-m4v': 'video/mp4', 'video/mpeg4': 'video/mp4', 'video/mov': 'video/quicktime',
}

// Un tipo que no dice nada: la fila no sirve para decidir y hay que mirar la respuesta.
export const SIN_VALOR = [
  '', 'application/octet-stream', 'binary/octet-stream', 'application/force-download',
  'application/download', 'application/unknown', 'application/x-download', '*/*',
]

// Lo que un navegador pinta DENTRO de una pestana en CUALQUIER aparato. TIENE QUE SER LA MISMA
// LISTA que la del servidor (`MIME_PREVISUALIZABLE`, en backend/services/adjuntosSeguros.js), no
// una version «prudente» mas corta: si el servidor manda un fichero con `inline` y con su tipo real
// y la pantalla decide descargarlo, la persona se queda sin ver algo que su navegador SI sabe
// ensenar Y ADEMAS lee un aviso que afirma lo contrario. Eso paso con el audio y el video, y la
// comparacion que tenia que haberlo impedido solo miraba en un sentido (de esta lista al servidor).
// Ahora la guardia las recorre EN LOS DOS SENTIDOS y exige que sean la MISMA:
// backend/routes/iphone-abrir-documento-pantalla.test.js, seccion 2.
//
// Lo que no entra en ninguna de las dos: HEIC, TIFF y DICOM. Safari los pinta y Chrome de escritorio
// no, asi que meterlos arreglaria el telefono del dueno y dejaria en blanco el ordenador de su
// recepcion. El servidor tampoco los manda `inline`: van en la categoria DOCUMENTO, con su tipo
// real y como descarga, que es lo que permite DECIR que son.
export const EN_PESTANA = [
  'application/pdf',
  'image/jpeg', 'image/pjpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/avif',
  // AUDIO Y VIDEO. El navegador los reproduce dentro de la pestana, en el telefono y en el
  // ordenador, y el servidor ya los mandaba asi. Faltaban aqui: una nota de voz o un video subido a
  // los documentos se descargaban, y encima con un aviso que decia —en falso— que el navegador no
  // sabia ensenarlos.
  'audio/webm', 'audio/ogg', 'audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/x-m4a',
  'audio/aac', 'audio/flac',
  'video/mp4', 'video/webm', 'video/quicktime',
]

const enPestana = new Set(EN_PESTANA)

// El tipo, escrito como toca.
export function normalizarTipo(mime) {
  const base = String(mime == null ? '' : mime).split(';')[0].trim().toLowerCase()
  return SINONIMO[base] || base
}

/** ¿Sabe el navegador ensenar esto dentro de una pestana? */
export const sePuedeVerDentro = (mime) => enPestana.has(normalizarTipo(mime))

// Se deja como expresion regular porque hay pantallas que ya la usaban asi (components/MisDocumentos).
export const PREVISUALIZABLE = { test: (m) => sePuedeVerDentro(m) }

// Tope de peso, y SOLO en iOS. En un ordenador un PDF de 40 MB se abre sin problema y bajarlo en vez
// de ensenarlo seria empeorar lo que hoy funciona. El numero es conservador, no medido: aqui no hay
// un iPhone con el que fijar el umbral de verdad (ver mensaje.txt de esta entrega).
export const MAX_PESTANA_IOS_MB = 25

// Como se llama en castellano lo que la persona tiene delante. Sin esto el aviso seria «no se puede
// previsualizar application/vnd.openxmlformats-officedocument.wordprocessingml.document», que no le
// dice nada a nadie.
//
// ESTO SI LLEVA TILDES Y EÑES. La norma de la casa de escribir sin acentos es para las CLAVES de
// esquema y los nombres de campo de una herramienta, donde un acento rompe cosas. Lo que lee una
// persona se escribe en castellano: si no, el mismo cuadro de aviso sale medio acentuado y medio no,
// porque las pantallas que lo pintan ya tienen texto con tildes tres lineas mas arriba.
const NOMBRE_DE_TIPO = [
  [/wordprocessingml|msword|opendocument\.text|application\/rtf|text\/rtf/i, marca('utils.abrirArchivo.un_documento_de_word', 'un documento de Word')],
  [/spreadsheetml|ms-excel|opendocument\.spreadsheet/i, marca('utils.abrirArchivo.una_hoja_de_calculo_de', 'una hoja de cálculo de Excel')],
  [/presentationml|ms-powerpoint|opendocument\.presentation/i, marca('utils.abrirArchivo.una_presentacion_de_powerpoint', 'una presentación de PowerPoint')],
  [/dicom/i, marca('utils.abrirArchivo.una_radiografia_en_formato_dicom', 'una radiografía en formato DICOM')],
  [/image\/(heic|heif)/i, marca('utils.abrirArchivo.una_foto_en_formato_heic', 'una foto en formato HEIC del iPhone')],
  [/image\/tiff/i, marca('utils.abrirArchivo.una_imagen_en_formato_tiff', 'una imagen en formato TIFF')],
  [/text\/csv/i, marca('utils.abrirArchivo.una_hoja_de_datos_csv', 'una hoja de datos CSV')],
  [/text\/plain/i, marca('utils.abrirArchivo.un_archivo_de_texto', 'un archivo de texto')],
  [/zip|rar|7z-compressed/i, marca('utils.abrirArchivo.un_archivo_comprimido', 'un archivo comprimido')],
  [/model\/(stl|obj)|application\/sla/i, marca('utils.abrirArchivo.un_modelo_en_3d', 'un modelo en 3D')],
]

// El nombre en castellano, o null si no se sabe qué es. NULL A PROPOSITO, y no un texto de relleno:
// con «un archivo que el navegador no sabe enseñar» de comodín, el aviso del caso MAS FRECUENTE
// —una fila antigua sin tipo y con un nombre sin extensión— quedaba en una tautología que no decía
// nada («es un archivo que el navegador no sabe enseñar, y el navegador no sabe enseñarlo»). Quien
// escribe el aviso necesita saber que aquí no hay nada que contar, para decir otra cosa.
export function nombreDeTipo(mime) {
  const m = String(mime == null ? '' : mime)
  for (const [re, texto] of NOMBRE_DE_TIPO) if (re.test(m)) return texto
  return null
}

// EL PESO QUE TRAE LA FILA, se llame como se llame la columna. La aplicación tiene DOS grafías vivas
// para lo mismo: `tamano` en los documentos del paciente, del tablón y del anestesista, y `bytes` en
// las derivaciones y en el portal del referidor (migración 2026-10-14-portal-referidor.sql). Cada
// pantalla escribía `tamano: fila.tamano` a mano y en las derivaciones eso valía `undefined`, así que
// el tope de peso del iPhone NO saltaba JAMAS justo donde se comparten radiografías y CBCT de hasta
// 30 MB. Se lee aquí, en un solo sitio, y no en once.
export function bytesDeFila(fila) {
  if (!fila || typeof fila !== 'object') return 0
  for (const clave of ['tamano', 'bytes', 'tamanio', 'size', 'peso', 'tamano_bytes', 'file_size']) {
    const n = Number(fila[clave])
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

// Un iPhone o un iPad. El iPad moderno miente en el userAgent y dice ser un Mac: se le pilla porque
// un Mac de verdad no tiene pantalla tactil.
export function esIOS(ventana) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  const nav = w && w.navigator
  if (!nav) return false
  const ua = String(nav.userAgent || '')
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return String(nav.platform || '') === 'MacIntel' && Number(nav.maxTouchPoints || 0) > 1
}

export const megas = (bytes) => {
  const n = Number(bytes)
  return Number.isFinite(n) && n > 0 ? n / (1024 * 1024) : 0
}

// POR QUE NO SE VA A PODER ABRIR DENTRO, decidido ANTES de pedir nada: 'tipo' | 'peso' | null.
// Se mira el tipo que dice la fila (que es lo unico que hay antes de bajarlo) y el peso que dice el
// listado. Si la fila no dice nada util, se es optimista y se abre la pestana igual: el tipo de
// verdad llega con la respuesta y todavia se esta a tiempo de cerrarla.
export function motivoNoAbre({ mime, tamano, ventana } = {}) {
  const declarado = normalizarTipo(mime)
  // `sin_valor` = la fila no dice nada util (vacia, `application/octet-stream`...). Ahi se es
  // OPTIMISTA y se abre la pestana igual: el tipo de verdad llega con la respuesta y todavia se
  // esta a tiempo de cerrarla. Cerrar la puerta aqui convertiria en descarga cualquier fichero
  // antiguo cuya fila se quedo sin tipo, que es media clinica.
  if (SIN_VALOR.includes(declarado)) return esIOS(ventana) && megas(tamano) > MAX_PESTANA_IOS_MB ? 'peso' : null
  if (declarado && !sePuedeVerDentro(declarado)) return 'tipo'
  if (esIOS(ventana) && megas(tamano) > MAX_PESTANA_IOS_MB) return 'peso'
  return null
}

// EL TEXTO QUE LEE LA PERSONA. Es la mitad del arreglo: sin esto, lo unico que cambia es que el
// error de Safari lo sustituye una descarga muda, y quien pulsa sigue pensando que el boton no va.
export function textoDeDescarga(motivo, { mime, tamano, nombre } = {}) {
  if (motivo === 'peso') {
    const mb = Math.round(megas(tamano))
    // SIN EL NOMBRE DEL FICHERO DENTRO. Un nombre de verdad («ESCANEO_CONTRATO_MUTUA_2026.pdf») es
    // una palabra de cuarenta letras que no se puede partir: en un telefono de 320 px desplaza la
    // pagina entera de lado. El nombre lo tiene delante, en la fila que acaba de pulsar.
    return i18nT('utils.abrirArchivo.el_archivo_pesa_mb_y', 'El archivo pesa {{mb}} MB y en un iPhone o un iPad el navegador no puede abrirlo dentro de una pestaña. Se ha descargado: lo tienes en la app Archivos de este dispositivo.', { mb })
  }
  if (motivo === 'bloqueada') {
    return i18nT('utils.abrirArchivo.el_navegador_no_ha_dejado', 'El navegador no ha dejado abrir la pestaña, así que el documento se ha descargado. Míralo en tus descargas.')
  }
  // CUANDO SI SE SABE QUE ES, se dice: es la diferencia entre «no se puede abrir» y «es un Word».
  // El sujeto de la frase es «Este archivo» (masculino) a proposito: asi el pronombre concuerda
  // diga lo que diga el nombre del tipo («una hoja de cálculo», «un documento»).
  const que = nombreDeTipo(mime)
  if (que) {
    return i18nT('utils.abrirArchivo.este_archivo_es_el_navegador', 'Este archivo es {{que}}. El navegador no puede mostrarlo dentro de una pestaña, así que se ha descargado: ábrelo desde las descargas de este dispositivo con el programa que le corresponda.', { que: tDato(que) })
  }
  // Y CUANDO NO SE SABE —el caso MAS FRECUENTE del parte del dueño: una fila antigua sin tipo y con
  // un nombre sin extensión reconocible— no se rellena con una tautología. Se dice lo único que es
  // cierto y lo único que le sirve: dónde está y qué hacer con él.
  return i18nT('utils.abrirArchivo.el_navegador_no_puede_mostrar', 'El navegador no puede mostrar este archivo dentro de una pestaña, así que se ha descargado. Está en las descargas de este dispositivo: ábrelo con el programa que le corresponda.')
}

// Guardar el fichero. Un <a download> no lo ejecuta jamas y, a diferencia de window.open, no lo
// bloquea el navegador del movil por venir despues de una espera.
export function descargarBlob(url, nombre, documento) {
  const doc = documento || (typeof document !== 'undefined' ? document : null)
  if (!doc) return false
  const a = doc.createElement('a')
  a.href = url
  a.download = nombre || 'documento'
  a.rel = 'noopener'
  if (doc.body && doc.body.appendChild) doc.body.appendChild(a)
  a.click()
  if (a.remove) a.remove()
  return true
}

// La pestana en blanco, DENTRO del gesto. `opener = null` para que el documento no pueda tocar la
// pagina que lo abrio.
export function abrirPestana(ventana) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  if (!w || typeof w.open !== 'function') return null
  let p = null
  try { p = w.open('', '_blank') } catch (_) { p = null }
  if (p) { try { p.opener = null } catch (_) { /* nada */ } }
  return p
}

// Al minuto, nunca antes: la pestana nueva todavia lo esta cargando (causa 3).
export const SOLTAR_MS = 60000

// ── LO QUE USAN LAS PANTALLAS ─────────────────────────────────────────────────────────────────
//
//  `traer` devuelve el Blob (cada pantalla tiene su llamada y su cabecera de sesion; aqui no se
//  sabe nada de eso). `avisar` recibe el texto cuando el documento se ha descargado en vez de
//  abrirse, y `alFallar` el error. `ventana`/`documento` existen para poder EJECUTAR esto en una
//  guardia sin navegador.
//
//  OJO AL ORDEN, QUE ES LA MITAD DEL ARREGLO: la pestana se abre en la PRIMERA linea, antes de
//  cualquier `await`. Una funcion `async` corre sincronamente hasta su primer `await`, asi que esta
//  llamada sigue estando dentro del gesto del usuario y el navegador no la bloquea.
export async function abrirArchivo({
  traer, nombre, mime, tamano, avisar, alFallar, alAcabar, ventana, documento,
} = {}) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  const doc = documento || (w && w.document) || (typeof document !== 'undefined' ? document : null)
  // DENTRO DE LA APP NATIVA NO HAY PESTAÑAS (27-sep-2026). Una «pestaña nueva» carga el documento en la MISMA vista, sin
  // barra ni botón de atrás, y hay que matar la app para salir: la trampa que el dueño vio con los PDF de WhatsApp. Ahí
  // el documento se enseña en el visor de la propia app (utils/verDocumento → components/VisorDocumentoGlobal), con su
  // «Cerrar». En el navegador —también el del iPhone, que sí tiene pestañas— todo sigue exactamente como antes.
  if (enLaAppNativa(w)) {
    try {
      const blob = await traer()
      if (!verDocumentoEnLaApp({ blob, nombre, mime: (blob && blob.type) || mime }, w)) throw new Error(i18nT('utils.abrirArchivo.no_se_puede_ensenar_el', 'No se puede enseñar el documento'))
      if (typeof alAcabar === 'function') alAcabar()
      return { abierto: true, descargado: false, motivo: null, enLaApp: true }
    } catch (e) {
      if (typeof alFallar === 'function') alFallar(e)
      return { abierto: false, descargado: false, motivo: 'error', error: e }
    }
  }
  const motivo = motivoNoAbre({ mime, tamano, ventana: w })
  const pestana = motivo ? null : abrirPestana(w)
  const cerrar = () => { if (pestana && !pestana.closed) { try { pestana.close() } catch (_) { /* nada */ } } }
  const decir = (texto) => { if (typeof avisar === 'function') avisar(texto) }

  try {
    const blob = await traer()
    const url = (w && w.URL ? w.URL : URL).createObjectURL(blob)
    const soltar = () => {
      const U = w && w.URL ? w.URL : URL
      // Con `w.setTimeout` desatado de su objeto, algunos navegadores lanzan «Illegal invocation»:
      // se llama siempre a traves de la ventana.
      const esperar = (w && typeof w.setTimeout === 'function')
        ? (f, ms) => w.setTimeout(f, ms)
        : (f, ms) => setTimeout(f, ms)
      esperar(() => { try { U.revokeObjectURL(url) } catch (_) { /* nada */ } }, SOLTAR_MS)
    }
    // EL TIPO DE VERDAD ES EL DE LA RESPUESTA, no el que decia la fila: la fila la escribio el
    // navegador de quien subio el fichero y puede mentir en las dos direcciones.
    const real = normalizarTipo(blob && blob.type)
    // EL PESO DE VERDAD, que hasta ahora solo se sabia si la fila lo traia. Un PDF que la aplicacion
    // FABRICA al vuelo (un presupuesto, el libro de estupefacientes de un trimestre) no tiene fila
    // ninguna: `tamano` llega `undefined` y el tope de iOS no podia saltar jamas. Con el blob
    // delante ya se sabe cuanto pesa, y todavia se esta a tiempo de cerrar la pestana y bajarlo.
    const pesaDeMas = esIOS(w) && megas((blob && blob.size) || tamano) > MAX_PESTANA_IOS_MB
    if (pestana && !pestana.closed && sePuedeVerDentro(real) && !pesaDeMas) {
      pestana.location.href = url
      soltar()
      if (typeof alAcabar === 'function') alAcabar()
      return { abierto: true, descargado: false, motivo: null }
    }
    cerrar()
    // Se descarga. Y el motivo que se DICE es el de verdad, no el que se supuso antes de pedirlo:
    // si la fila decia una cosa y la respuesta dice otra, manda la respuesta.
    const porQue = (motivo === 'peso' || pesaDeMas) ? 'peso'
      : (!pestana && !motivo ? 'bloqueada' : 'tipo')
    descargarBlob(url, nombre || 'documento', doc)
    decir(textoDeDescarga(porQue, { mime: real || mime, tamano: (blob && blob.size) || tamano, nombre }))
    soltar()
    if (typeof alAcabar === 'function') alAcabar()
    return { abierto: false, descargado: true, motivo: porQue }
  } catch (e) {
    cerrar()
    if (typeof alFallar === 'function') alFallar(e)
    return { abierto: false, descargado: false, motivo: 'error', error: e }
  }
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LOS PDF QUE LA APLICACION FABRICA AL VUELO (B-443)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Un presupuesto, una receta, un justificante, una factura, el libro de estupefacientes: no son
//  ficheros guardados, los construye el servidor en el momento y llegan como blob. Veintitantas
//  pantallas hacian lo mismo, cada una a su manera y TODAS con el mismo fallo de B-435:
//
//      const r = await api.get('.../pdf', { responseType: 'blob' })
//      const url = URL.createObjectURL(r.data)
//      window.open(url, '_blank')            <-- despues del await: el iPhone la bloquea EN SILENCIO
//
//  En un ordenador funciona siempre, asi que no se ve nunca desde aqui. En un iPhone se pulsa
//  «Ver PDF» y no pasa absolutamente nada: ni pestana, ni error, ni aviso. Es el mismo fallo que el
//  dueno reporto con los documentos subidos, en otra pantalla.
//
//  Aqui el tipo no hay que adivinarlo —siempre es un PDF— y el peso no se sabe hasta que llega,
//  asi que lo mide `abrirArchivo` con el blob delante. Lo unico que pone cada pantalla es de donde
//  se trae, como se llama el fichero si hay que bajarlo, y por donde avisa.
export function abrirPdfGenerado({ traer, nombre, avisar, alFallar, alAcabar, ventana, documento } = {}) {
  return abrirArchivo({
    traer, nombre: nombre || 'documento.pdf', mime: 'application/pdf',
    avisar, alFallar, alAcabar, ventana, documento,
  })
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «DESCARGAR» UN PDF QUE FABRICA LA APLICACION, EN CUALQUIER APARATO (12-oct-2026, t194)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Los botones que BAJAN un PDF (no los que lo enseñan) lo hacian con un <a download> de un blob. En un
//  ordenador funciona. En la app de Biango del movil y en la app instalada en la pantalla de inicio de
//  un iPhone NO HACE NADA: ni descarga, ni abre, ni avisa. El dueño, con la copia de una factura
//  traida: «me sale el boton pero al pulsar no la descarga». Aqui cada aparato recibe el PDF como sabe:
//   · 'descarga': en un ordenador o en el navegador de un Android, se descarga con su nombre, como antes;
//   · 'visor': en la app de Biango y en la app instalada de un iPhone o un iPad, en el visor de la
//     aplicacion (components/VisorDocumentoGlobal), con «Cerrar» y «Compartir» o «Descargar»;
//   · 'pestana': en el navegador del iPhone, por `abrirPdfGenerado` (la pestaña dentro del toque).
//  REGLA PARA QUIEN LA LLAME: se llama DENTRO del toque, antes de cualquier `await`, y la peticion va
//  escrita dentro de `traer` (si se pide antes, en el iPhone la pestaña sale tarde y se bloquea).
//  Lo mide backend/services/copia-factura-traida-se-entrega.test.js (§2 y §6).
function instaladaEnInicio(w) {
  try {
    if (w && w.navigator && w.navigator.standalone === true) return true
    return !!(w && typeof w.matchMedia === 'function' && w.matchMedia('(display-mode: standalone)').matches)
  } catch (_) { return false }
}

/** Como recibe un PDF este aparato: 'visor' | 'pestana' | 'descarga'. */
export function comoSeEntregaUnPdf(ventana) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  if (!w) return 'descarga'
  if (enLaAppNativa(w)) return 'visor'
  if (esIOS(w)) return instaladaEnInicio(w) ? 'visor' : 'pestana'
  return 'descarga'
}

export function descargarPdfGenerado({ traer, nombre, avisar, alFallar, alAcabar, ventana, documento } = {}) {
  const w = ventana || (typeof window !== 'undefined' ? window : null)
  const modo = comoSeEntregaUnPdf(w)
  if (modo === 'pestana') return abrirPdfGenerado({ traer, nombre, avisar, alFallar, alAcabar, ventana: w || undefined, documento })
  const doc = documento || (w && w.document) || undefined
  const decir = (texto) => { if (typeof avisar === 'function') avisar(texto) }
  return (async () => {
    try {
      const blob = await traer()
      const n = nombre || 'documento.pdf'
      if (modo === 'visor' && verDocumentoEnLaApp({ blob, nombre: n, mime: 'application/pdf' }, w)) {
        if (typeof alAcabar === 'function') alAcabar()
        return { abierto: true, descargado: false, motivo: null, enLaApp: true }
      }
      const U = (w && w.URL) ? w.URL : URL
      const url = U.createObjectURL(blob)
      descargarBlob(url, n, doc)
      // Sin visor al que pedírselo (no debería pasar): se ha descargado, y se dice.
      if (modo === 'visor') decir(textoDeDescarga('bloqueada', { mime: 'application/pdf', nombre: n }))
      // Se suelta al minuto, nunca antes (SOLTAR_MS); siempre a traves de la ventana (ver abrirArchivo).
      const t = (w && typeof w.setTimeout === 'function')
        ? w.setTimeout(() => { try { U.revokeObjectURL(url) } catch (_) { /* nada */ } }, SOLTAR_MS)
        : setTimeout(() => { try { U.revokeObjectURL(url) } catch (_) { /* nada */ } }, SOLTAR_MS)
      if (t && typeof t.unref === 'function') t.unref()
      if (typeof alAcabar === 'function') alAcabar()
      return { abierto: false, descargado: true, motivo: modo === 'visor' ? 'bloqueada' : null }
    } catch (e) {
      if (typeof alFallar === 'function') alFallar(e)
      return { abierto: false, descargado: false, motivo: 'error', error: e }
    }
  })()
}

// EL MOTIVO DE UN FALLO, cuando la peticion pedia un PDF: el cuerpo de la respuesta llega como Blob y
// `msgError` (utils/errores) no lo puede leer. Devuelve el mismo error con el cuerpo ya leido (JSON si
// lo es; si no, su texto), para que se pueda decir lo que contesto el servidor.
export async function errorConCuerpo(e) {
  const d = e && e.response ? e.response.data : null
  if (!d || typeof d !== 'object' || typeof d.text !== 'function') return e
  let leido = null
  try {
    const t = await d.text()
    try { leido = JSON.parse(t) } catch (_) { leido = t }
  } catch (_) { leido = null }
  return { ...e, message: e.message, response: { ...e.response, data: leido } }
}
